import { useState, useEffect, useRef, useCallback } from 'react';
import SectionHeader from '../../components/admin/SectionHeader';
import DataTable from '../../components/admin/DataTable';
import Badge from '../../components/admin/Badge';
import SupportExportModal from './SupportExportModal';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

const getSupportTier = (planSlug) => {
  if (!planSlug) return null;
  const slug = planSlug.toLowerCase();
  if (slug.includes('business')) return { label: 'Fast-Response', desc: 'Same-day', color: '#10b981', bg: 'rgba(16,185,129,0.10)', border: 'rgba(16,185,129,0.20)' };
  if (slug.includes('pro'))      return { label: 'Priority', desc: '24hr', color: '#eab308', bg: 'rgba(234,179,8,0.10)', border: 'rgba(234,179,8,0.20)' };
  return { label: 'Community', desc: '2-5 days', color: '#71717a', bg: 'rgba(113,113,122,0.08)', border: 'rgba(113,113,122,0.15)' };
};

function AdminSupport({ apiFetch, token, toast, initialTicketId, refreshSupportUnread }) {
  const [supLoading, setSupLoading] = useState(true);
  const [showExport, setShowExport] = useState(false);
  const [supTickets, setSupTickets] = useState([]);
  const [supSelectedTicket, setSupSelectedTicket] = useState(null);
  const [supReplies, setSupReplies] = useState([]);
  const [supReplyMsg, setSupReplyMsg] = useState('');
  const supReplyTextareaRef = useRef(null);
  const [supStatusFilter, setSupStatusFilter] = useState('all');
  const [supPriorityFilter, setSupPriorityFilter] = useState('all');
  const [supEditStatus, setSupEditStatus] = useState('');
  const [supEditPriority, setSupEditPriority] = useState('');
  const [supReplyAttachments, setSupReplyAttachments] = useState([]);
  const [supLightbox, setSupLightbox] = useState(null);
  const [supSubDetails, setSupSubDetails] = useState(null);
  const [supSubLoading, setSupSubLoading] = useState(false);
  const [supReplySending, setSupReplySending] = useState(false);
  const supChatBottomRef = useRef(null);
  const supPollRef = useRef(null);
  const [supTypingIndicator, setSupTypingIndicator] = useState(false);
  const supTypingTimerRef = useRef(null);
  const supTypingDebounceRef = useRef(null);
  const [supSupportStatus, setSupSupportStatus] = useState('online');

  // Create ticket on behalf of customer
  const [supShowCreate, setSupShowCreate] = useState(false);
  const [supCreateEmail, setSupCreateEmail] = useState('');
  const [supCreateSubject, setSupCreateSubject] = useState('');
  const [supCreateMessage, setSupCreateMessage] = useState('');
  const [supCreateCategory, setSupCreateCategory] = useState('general');
  const [supCreatePriority, setSupCreatePriority] = useState('normal');
  const [supCreatePlanSlug, setSupCreatePlanSlug] = useState('');
  const [supCreateDomain, setSupCreateDomain] = useState('');
  const [supCreating, setSupCreating] = useState(false);
  const [supReviewSending, setSupReviewSending] = useState(false);

  // Full-text search across the whole ticket corpus (subject + all messages).
  const [supSearch, setSupSearch] = useState('');
  const searchDebounceRef = useRef(null);

  const loadTickets = useCallback(async (q = '') => {
    setSupLoading(true);
    try {
      const url = q && q.trim()
        ? `/api/admin/support/tickets?search=${encodeURIComponent(q.trim())}`
        : '/api/admin/support/tickets';
      const r = await apiFetch(url);
      const data = await r.json();
      setSupTickets(data.tickets || []);
    } catch { toast.error('Error', 'Failed to load tickets'); }
    finally { setSupLoading(false); }
  }, [apiFetch, toast]);

  const onSearchChange = (v) => {
    setSupSearch(v);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => loadTickets(v), 300);
  };

  useEffect(() => {
    loadTickets('');
    apiFetch('/api/support/status').then(r => r.json()).then(d => setSupSupportStatus(d.status || 'online')).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-open ticket when initialTicketId is set (e.g. from notification click)
  useEffect(() => {
    if (!initialTicketId) return;
    apiFetch(`/api/admin/support/tickets/${initialTicketId}`)
      .then(r => r.json())
      .then(data => {
        if (data.ticket) {
          setSupSelectedTicket(data.ticket);
          setSupEditStatus(data.ticket.status);
          setSupEditPriority(data.ticket.priority);
          setSupReplies(data.replies || []);
        }
      })
      .catch(() => {});
  }, [initialTicketId]);

  const supChatMessagesRef = useRef(null);
  const supIsAtBottomRef = useRef(true);
  const scrollSupToBottom = () => {
    setTimeout(() => supChatBottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 30);
  };

  const reloadRepliesSilent = useCallback(async (ticketId) => {
    try {
      const wasAtBottom = supIsAtBottomRef.current;
      const res = await apiFetch(`/api/admin/support/tickets/${ticketId}`);
      const data = await res.json();
      setSupReplies(data.replies || []);
      if (wasAtBottom) scrollSupToBottom();
    } catch {}
  }, [apiFetch]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTicket = async (ticket) => {
    setSupSelectedTicket(ticket); setSupEditStatus(ticket.status); setSupEditPriority(ticket.priority);
    setSupSubDetails(null);
    clearInterval(supPollRef.current);
    try {
      const res = await apiFetch(`/api/admin/support/tickets/${ticket.id}`);
      const data = await res.json();
      setSupReplies(data.replies || []);
      if (data.ticket) { setSupSelectedTicket(data.ticket); setSupEditStatus(data.ticket.status); setSupEditPriority(data.ticket.priority); }
      setTimeout(() => supChatBottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
      apiFetch(`/api/admin/support/tickets/${ticket.id}/read`, { method: 'POST' }).catch(() => {});
      setSupTickets(prev => prev.map(t => t.id === ticket.id ? { ...t, unread_count: 0 } : t));
      if (refreshSupportUnread) refreshSupportUnread();
    } catch { toast.error('Error', 'Failed to load replies'); }
    // Start polling
    supPollRef.current = setInterval(() => reloadRepliesSilent(ticket.id), 10000);
    // Fetch subscription details for the domain
    if (ticket.domain_name) {
      setSupSubLoading(true);
      try {
        const res = await apiFetch('/api/admin/subscriptions');
        const data = await res.json();
        const subs = data.subscriptions || [];
        const match = subs.find(s => s.domain && s.domain.toLowerCase() === ticket.domain_name.toLowerCase());
        setSupSubDetails(match || null);
      } catch { /* silent */ }
      finally { setSupSubLoading(false); }
    }
  };

  const reloadReplies = async (ticketId) => {
    try {
      const res = await apiFetch(`/api/admin/support/tickets/${ticketId}`);
      const data = await res.json();
      setSupReplies(data.replies || []);
    } catch {}
  };

  // Keep ref to selected ticket ID for stale-closure-safe event handler
  const supSelectedIdRef = useRef(null);
  useEffect(() => { supSelectedIdRef.current = supSelectedTicket?.id || null; }, [supSelectedTicket?.id]);

  // Track whether admin is at the bottom of the chat (captured before DOM changes)
  useEffect(() => {
    const el = supChatMessagesRef.current;
    if (!el) return;
    const onScroll = () => {
      supIsAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [supSelectedTicket?.id]);

  // Listen to SSE broadcast events for instant ticket reply updates
  useEffect(() => {
    const onNotif = (e) => {
      const d = e.detail;
      if (!d) return;
      if (d.event === 'typing' && d.ticketId === supSelectedIdRef.current) {
        setSupTypingIndicator(true);
        clearTimeout(supTypingTimerRef.current);
        supTypingTimerRef.current = setTimeout(() => setSupTypingIndicator(false), 3500);
      }
      // User read admin messages — refresh to show double tick immediately
      if (d.event === 'read' && d.ticketId === supSelectedIdRef.current) {
        reloadRepliesSilent(supSelectedIdRef.current);
      }
      const type = d?.type || '';
      if ((type === 'ticket_reply' || type === 'ticket_new') && supSelectedIdRef.current) {
        reloadRepliesSilent(supSelectedIdRef.current);
      }
    };
    window.addEventListener('openhosst:notification', onNotif);
    return () => window.removeEventListener('openhosst:notification', onNotif);
  }, [reloadRepliesSilent]);

  const handleSupReplyFileSelect = (e) => {
    const files = [...e.target.files].filter(f => /^image\/(png|jpe?g|gif|webp)$/.test(f.type)).slice(0, 5);
    setSupReplyAttachments(prev => [...prev, ...files].slice(0, 5));
    e.target.value = '';
  };

  const unreadInList = supTickets.filter(t => t.unread_count > 0).length;
  const filteredTickets = supTickets.filter(t => {
    if (supStatusFilter === 'unread') { if (!(t.unread_count > 0)) return false; }
    else if (supStatusFilter !== 'all' && t.status !== supStatusFilter) return false;
    if (supPriorityFilter !== 'all' && t.priority !== supPriorityFilter) return false;
    return true;
  });

  const ticketColumns = [
    { key: 'subject', header: 'Subject', render: row => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {row.unread_count > 0 && (
            <span title={`${row.unread_count} unread message${row.unread_count > 1 ? 's' : ''}`} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '18px', height: '18px', padding: '0 5px', borderRadius: '9px', background: '#ef4444', color: '#fff', fontSize: '10px', fontWeight: 700 }}>{row.unread_count}</span>
          )}
          <span style={{ fontWeight: row.unread_count > 0 ? 700 : 600 }}>{row.subject}</span>
        </span>
        {row.match_snippet && (
          <span style={{ fontSize: '11px', color: 'var(--admin-text-muted)' }}>
            <i className="fa-solid fa-magnifying-glass" style={{ marginRight: 4, opacity: 0.6 }} />
            <span style={{ opacity: 0.7 }}>{row.match_field}:</span> {row.match_snippet}
          </span>
        )}
      </div>
    ) },
    { key: 'user_email', header: 'User', cellStyle: { color: 'var(--admin-text-secondary)' } },
    { key: 'plan', header: 'Plan', render: row => row.plan_slug ? <span style={{ fontSize: '11px', color: 'var(--admin-accent)' }}>{row.plan_slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</span> : '—' },
    { key: 'sla', header: 'SLA', render: row => { const tier = getSupportTier(row.plan_slug); return tier ? <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '6px', background: tier.bg, border: '1px solid ' + tier.border, color: tier.color, fontWeight: 600, whiteSpace: 'nowrap' }}>{tier.label}</span> : '—'; }},
    { key: 'status', header: 'Status', render: row => <Badge variant={row.status === 'open' ? 'pending' : row.status === 'resolved' ? 'active' : 'info'}>{row.status}</Badge> },
    { key: 'priority', header: 'Priority', render: row => <Badge variant={row.priority === 'high' ? 'suspended' : row.priority === 'medium' ? 'pending' : 'info'}>{row.priority}</Badge> },
    { key: 'created_at', header: 'Created', cellStyle: { color: 'var(--admin-text-muted)' }, render: row => new Date(row.created_at).toLocaleDateString() },
    { key: 'actions', header: 'Action', render: row => <button className="admin-btn admin-btn--sm admin-btn--secondary" onClick={() => loadTicket(row)}>View</button> },
  ];

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!supCreateEmail.trim() || !supCreateSubject.trim() || !supCreateMessage.trim()) return;
    setSupCreating(true);
    try {
      const res = await apiFetch('/api/admin/support/tickets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_email: supCreateEmail.trim(),
          subject: supCreateSubject.trim(),
          message: supCreateMessage.trim(),
          category: supCreateCategory,
          priority: supCreatePriority,
          plan_slug: supCreatePlanSlug.trim(),
          domain_name: supCreateDomain.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error('Error', data.error || 'Failed to create ticket'); return; }
      toast.success('Created', `Ticket #${data.ticketId} created`);
      setSupShowCreate(false);
      setSupCreateEmail(''); setSupCreateSubject(''); setSupCreateMessage('');
      setSupCreateCategory('general'); setSupCreatePriority('normal');
      setSupCreatePlanSlug(''); setSupCreateDomain('');
      // Reload ticket list
      const tr = await apiFetch('/api/admin/support/tickets');
      const td = await tr.json();
      setSupTickets(td.tickets || []);
    } catch { toast.error('Error', 'Failed to create ticket'); }
    finally { setSupCreating(false); }
  };

  return (
    <div>
      <SectionHeader
        title="Support"
        actions={
          <button className="admin-btn admin-btn--secondary admin-btn--sm" onClick={() => setShowExport(true)}>
            <i className="fa-solid fa-file-export" style={{ marginRight: 6 }} />Export data
          </button>
        }
      />

      {showExport && (
        <SupportExportModal
          apiFetch={apiFetch}
          toast={toast}
          categories={[...new Set(supTickets.map(t => t.category).filter(Boolean))].sort()}
          onClose={() => setShowExport(false)}
        />
      )}

      {/* Global agent availability status — always visible */}
      <div className="admin-card" style={{marginBottom:'12px',padding:'10px 16px',display:'flex',alignItems:'center',gap:'12px',flexWrap:'wrap'}}>
        <div style={{display:'flex',alignItems:'center',gap:'7px',flexShrink:0}}>
          <span style={{
            width:'9px',height:'9px',borderRadius:'50%',flexShrink:0,display:'inline-block',
            background: supSupportStatus==='online'?'#10b981':supSupportStatus==='away'?'#f59e0b':supSupportStatus==='busy'?'#f97316':'#6b7280',
            boxShadow: supSupportStatus==='online'?'0 0 0 3px rgba(16,185,129,0.2)':supSupportStatus==='away'?'0 0 0 3px rgba(245,158,11,0.2)':supSupportStatus==='busy'?'0 0 0 3px rgba(249,115,22,0.2)':'none'
          }}/>
          <span style={{fontSize:'13px',fontWeight:'600',color:'var(--admin-text-primary)'}}>
            {supSupportStatus==='online'?'Online':supSupportStatus==='away'?'Away':supSupportStatus==='busy'?'Busy':'Offline'}
          </span>
          <span style={{fontSize:'12px',color:'var(--admin-text-muted)'}}>
            — {supSupportStatus==='online'?'Responding to tickets':'Response times may be delayed'}
          </span>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:'6px',marginLeft:'auto'}}>
          <span style={{fontSize:'11px',color:'var(--admin-text-muted)'}}>Set status:</span>
          {[
            {s:'online',label:'Online',color:'#10b981'},
            {s:'away',label:'Away',color:'#f59e0b'},
            {s:'busy',label:'Busy',color:'#f97316'},
            {s:'offline',label:'Offline',color:'#6b7280'},
          ].map(({s,label,color}) => (
            <button key={s}
              onClick={() => {
                setSupSupportStatus(s);
                apiFetch('/api/admin/support/status', { method: 'PATCH', headers: {'Content-Type':'application/json'}, body: JSON.stringify({ status: s }) });
              }}
              style={{
                fontSize:'11px',padding:'3px 10px',borderRadius:'6px',cursor:'pointer',fontWeight:'600',
                border: supSupportStatus===s ? `1px solid ${color}` : '1px solid var(--admin-border)',
                background: supSupportStatus===s ? `${color}22` : 'transparent',
                color: supSupportStatus===s ? color : 'var(--admin-text-secondary)',
                transition:'all 0.15s'
              }}
            >{label}</button>
          ))}
        </div>
      </div>

      {supSelectedTicket ? (
        <div className="admin-card">
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'12px',flexWrap:'wrap',gap:'8px'}}>
            <button className="admin-btn admin-btn--ghost" onClick={() => { setSupSelectedTicket(null); setSupReplies([]); setSupReplyMsg(''); setSupReplyAttachments([]); clearInterval(supPollRef.current); }}>
              <i className="fa-solid fa-arrow-left" style={{marginRight:4}} /> Back to list
            </button>
            <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
              <label style={{display:'inline-flex',alignItems:'center',gap:'7px',cursor:'pointer',userSelect:'none'}}
                title={supSelectedTicket.admin_email_notifications !== 0 ? 'Email updates ON — click to disable' : 'Email updates OFF — click to enable'}>
                <input type="checkbox"
                  checked={supSelectedTicket.admin_email_notifications !== 0}
                  style={{display:'none'}}
                  onChange={() => {
                    const newVal = supSelectedTicket.admin_email_notifications === 0 ? 1 : 0;
                    setSupSelectedTicket(prev => ({ ...prev, admin_email_notifications: newVal }));
                    apiFetch(`/api/admin/support/tickets/${supSelectedTicket.id}/email-notifications`, { method: 'PATCH', headers: {'Content-Type':'application/json'}, body: JSON.stringify({ enabled: newVal === 1 }) });
                  }}
                />
                <span style={{
                  position:'relative', width:'32px', height:'18px', flexShrink:0,
                  background: supSelectedTicket.admin_email_notifications !== 0 ? 'rgba(64,224,208,0.2)' : 'rgba(255,255,255,0.08)',
                  borderRadius:'9px', border: supSelectedTicket.admin_email_notifications !== 0 ? '1px solid rgba(64,224,208,0.4)' : '1px solid rgba(255,255,255,0.15)',
                  display:'inline-block', transition:'all 0.2s'
                }}>
                  <span style={{
                    position:'absolute', top:'2px',
                    left: supSelectedTicket.admin_email_notifications !== 0 ? '16px' : '2px',
                    width:'12px', height:'12px', borderRadius:'50%',
                    background: supSelectedTicket.admin_email_notifications !== 0 ? '#40E0D0' : '#666',
                    transition:'all 0.2s'
                  }}/>
                </span>
                <span style={{fontSize:'11px', color: supSelectedTicket.admin_email_notifications !== 0 ? '#40E0D0' : 'var(--admin-text-muted)', whiteSpace:'nowrap'}}>
                  <i className="fa-solid fa-envelope" style={{marginRight:'4px'}}/>
                  Email updates
                </span>
              </label>
            </div>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <h3 style={{ margin: '0 0 8px 0', color: 'var(--admin-text-primary)', fontSize: '16px' }}>{supSelectedTicket.subject}</h3>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', fontSize: '12px', color: 'var(--admin-text-secondary)' }}>
              <span>{supSelectedTicket.user_email}</span>
              <span>{new Date(supSelectedTicket.created_at).toLocaleString()}</span>
              <Badge variant={supSelectedTicket.status === 'open' ? 'pending' : supSelectedTicket.status === 'resolved' ? 'active' : 'info'}>{supSelectedTicket.status}</Badge>
              <Badge variant={supSelectedTicket.priority === 'high' ? 'suspended' : supSelectedTicket.priority === 'medium' ? 'pending' : 'info'}>{supSelectedTicket.priority}</Badge>
            </div>
            {(supSelectedTicket.plan_slug || supSelectedTicket.domain_name) && (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px', fontSize: '12px' }}>
                {supSelectedTicket.plan_slug && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 10px', borderRadius: '6px', background: 'var(--admin-accent-soft)', border: '1px solid var(--admin-accent-border)', color: 'var(--admin-accent)', fontWeight: 600 }}>
                    <i className="fa-solid fa-cube" style={{ fontSize: '10px' }} /> {supSelectedTicket.plan_slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                  </span>
                )}
                {supSelectedTicket.domain_name && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 10px', borderRadius: '6px', background: 'var(--admin-elevated)', border: '1px solid var(--admin-border)', color: 'var(--admin-text-secondary)' }}>
                    <i className="fa-solid fa-globe" style={{ fontSize: '10px' }} /> {supSelectedTicket.domain_name}
                  </span>
                )}
                {getSupportTier(supSelectedTicket.plan_slug) && (() => { const tier = getSupportTier(supSelectedTicket.plan_slug); return (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 10px', borderRadius: '6px', background: tier.bg, border: '1px solid ' + tier.border, color: tier.color, fontWeight: 600 }}>
                    <i className="fa-solid fa-headset" style={{ fontSize: '10px' }} /> {tier.label}
                  </span>
                );})()}
              </div>
            )}
          </div>

          {/* Subscription info panel */}
          {supSelectedTicket.domain_name && (
            <div style={{ marginBottom: '16px', padding: '10px 14px', borderRadius: '8px', background: 'var(--admin-elevated)', border: '1px solid var(--admin-border)', fontSize: '12px' }}>
              {supSubLoading ? (
                <span style={{ color: 'var(--admin-text-muted)' }}>Loading subscription info…</span>
              ) : supSubDetails ? (() => {
                const stripeColor = supSubDetails.status === 'active' ? '#10b981' : supSubDetails.status === 'trialing' ? '#60a5fa' : supSubDetails.status === 'past_due' ? '#f59e0b' : supSubDetails.status === 'canceled' ? '#ef4444' : '#71717a';
                const svcColor = supSubDetails.service_status === 'active' ? '#10b981' : supSubDetails.service_status === 'pending' ? '#f59e0b' : supSubDetails.service_status === 'suspended' ? '#ef4444' : '#71717a';
                return (
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={{ color: 'var(--admin-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '10px' }}>Subscription</span>
                    {supSubDetails.srv && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: 'var(--admin-text-secondary)' }}>
                        <i className="fa-solid fa-server" style={{ fontSize: '10px', color: 'var(--admin-accent)' }} />
                        <strong style={{ color: 'var(--admin-text-primary)' }}>SRV:</strong> {supSubDetails.srv}
                      </span>
                    )}
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <i className="fa-brands fa-stripe-s" style={{ fontSize: '10px', color: stripeColor }} />
                      <strong style={{ color: 'var(--admin-text-primary)' }}>Stripe:</strong>
                      <span style={{ color: stripeColor, fontWeight: 600, textTransform: 'capitalize' }}>{supSubDetails.status}</span>
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <i className="fa-solid fa-circle" style={{ fontSize: '8px', color: svcColor }} />
                      <strong style={{ color: 'var(--admin-text-primary)' }}>Status:</strong>
                      <span style={{ color: svcColor, fontWeight: 600, textTransform: 'capitalize' }}>{(supSubDetails.service_status || '—').replace(/_/g, ' ')}</span>
                    </span>
                    <span style={{ color: 'var(--admin-text-muted)', fontFamily: 'monospace', fontSize: '11px' }}>{supSubDetails.id}</span>
                  </div>
                );
              })() : (
                <span style={{ color: 'var(--admin-text-muted)' }}>No subscription found for this domain.</span>
              )}
            </div>
          )}

          {/* Status/Priority controls */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <label className="admin-label">STATUS</label>
              <select className="admin-select" value={supEditStatus} onChange={e => setSupEditStatus(e.target.value)}>
                <option value="open">Open</option><option value="in_progress">In Progress</option><option value="resolved">Resolved</option><option value="closed">Closed</option>
              </select>
            </div>
            <div>
              <label className="admin-label">PRIORITY</label>
              <select className="admin-select" value={supEditPriority} onChange={e => setSupEditPriority(e.target.value)}>
                <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
              </select>
            </div>
            <button className="admin-btn admin-btn--primary admin-btn--sm" onClick={async () => {
              try {
                await apiFetch(`/api/admin/support/tickets/${supSelectedTicket.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: supEditStatus, priority: supEditPriority }) });
                setSupSelectedTicket(prev => ({ ...prev, status: supEditStatus, priority: supEditPriority }));
                setSupTickets(prev => prev.map(t => t.id === supSelectedTicket.id ? { ...t, status: supEditStatus, priority: supEditPriority } : t));
                toast.success('Updated', 'Ticket updated');
              } catch { toast.error('Error', 'Update failed'); }
            }}>Update</button>
            {supSelectedTicket.status === 'closed' && (
              <button
                className="admin-btn admin-btn--sm"
                style={{ background: '#00b67a', color: '#fff', border: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px', opacity: supReviewSending ? 0.7 : 1 }}
                disabled={supReviewSending}
                onClick={async () => {
                  setSupReviewSending(true);
                  try {
                    const r = await apiFetch(`/api/admin/support/tickets/${supSelectedTicket.id}/request-review`, { method: 'POST' });
                    if (r.ok) toast.success('Sent!', 'Review request emailed to customer');
                    else toast.error('Error', 'Failed to send review request');
                  } catch { toast.error('Error', 'Failed to send review request'); }
                  finally { setSupReviewSending(false); }
                }}
              >
                <i className="fa-solid fa-star" /> {supReviewSending ? 'Sending…' : 'Ask for Review'}
              </button>
            )}
          </div>

          {/* Chat bubble thread */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0', marginBottom: '0',
            background: 'var(--admin-bg)', borderRadius: '12px', border: '1px solid var(--admin-border)',
            overflow: 'hidden', minHeight: '320px', maxHeight: '460px' }}>
            <div ref={supChatMessagesRef} style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '380px' }}>
              {supReplies.length === 0 && (
                <div style={{ color: 'var(--admin-text-muted)', fontSize: '13px', textAlign: 'center', padding: '40px 0' }}>No messages yet.</div>
              )}
              {supReplies.map((r, idx) => {
                const isAdmin = r.sender_type === 'admin';
                const showDate = idx === 0 || new Date(r.created_at).toDateString() !== new Date(supReplies[idx-1].created_at).toDateString();
                const time = new Date(r.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                return (
                  <div key={r.id}>
                    {showDate && (
                      <div style={{ textAlign: 'center', margin: '12px 0 8px', fontSize: '11px', color: 'var(--admin-text-muted)' }}>
                        <span style={{ background: 'var(--admin-elevated)', padding: '2px 12px', borderRadius: '20px', border: '1px solid var(--admin-border)' }}>
                          {new Date(r.created_at).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
                        </span>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: isAdmin ? 'flex-end' : 'flex-start', marginBottom: '6px', gap: '8px', alignItems: 'flex-end' }}>
                      {!isAdmin && (
                        <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(96,165,250,0.15)', border: '1px solid rgba(96,165,250,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <i className="fa-solid fa-user" style={{ fontSize: '11px', color: '#60a5fa' }} />
                        </div>
                      )}
                      <div style={{ maxWidth: '72%' }}>
                        {!isAdmin && <div style={{ fontSize: '10px', color: 'var(--admin-text-muted)', marginBottom: '3px', paddingLeft: '4px' }}>{r.sender_email}</div>}
                        <div style={{
                          padding: '10px 14px', borderRadius: isAdmin ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                          background: isAdmin ? 'var(--admin-accent)' : 'var(--admin-elevated)',
                          color: isAdmin ? '#000' : 'var(--admin-text-primary)',
                          border: isAdmin ? 'none' : '1px solid var(--admin-border)',
                          fontSize: '13px', lineHeight: '1.5', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        }}>
                          {r.message}
                          {r.attachments && r.attachments.length > 0 && (
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: r.message ? '8px' : '0' }}>
                              {r.attachments.map((src, i) => (
                                <img key={i} src={`${API_URL}${src}`} alt="attachment"
                                  style={{ width: '80px', height: '60px', objectFit: 'cover', borderRadius: '6px', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.1)' }}
                                  onClick={() => setSupLightbox(`${API_URL}${src}`)} />
                              ))}
                            </div>
                          )}
                        </div>
                        <div style={{ fontSize: '10px', color: 'var(--admin-text-muted)', marginTop: '3px', textAlign: isAdmin ? 'right' : 'left', paddingLeft: isAdmin ? 0 : '4px', paddingRight: isAdmin ? '4px' : 0 }}>
                          {time}
                          {isAdmin && r.read_at && (
                            <span style={{marginLeft:'4px',display:'inline-flex',alignItems:'center',gap:'-2px'}}>
                              <i className="fa-solid fa-check" style={{fontSize:'9px'}}/>
                              <i className="fa-solid fa-check" style={{fontSize:'9px',color:'#40E0D0',marginLeft:'-4px'}}/>
                            </span>
                          )}
                        </div>
                      </div>
                      {isAdmin && (
                        <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(64,224,208,0.15)', border: '1px solid rgba(64,224,208,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <i className="fa-solid fa-headset" style={{ fontSize: '11px', color: 'var(--admin-accent)' }} />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {supTypingIndicator && (
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', marginBottom: '6px' }}>
                  <div style={{width:'28px',height:'28px',borderRadius:'50%',background:'rgba(96,165,250,0.15)',border:'1px solid rgba(96,165,250,0.3)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                    <i className="fa-solid fa-user" style={{fontSize:'11px',color:'#60a5fa'}}/>
                  </div>
                  <div style={{padding:'10px 14px',borderRadius:'18px 18px 18px 4px',background:'var(--admin-elevated)',border:'1px solid var(--admin-border)'}}>
                    <span style={{display:'inline-flex',gap:'4px',alignItems:'center'}}>
                      {[0,1,2].map(i => <span key={i} style={{width:'6px',height:'6px',borderRadius:'50%',background:'var(--admin-text-muted)',animation:`typing-dot 1.2s ${i*0.2}s infinite`}}/>)}
                    </span>
                  </div>
                </div>
              )}
              <div ref={supChatBottomRef} />
            </div>

            {/* Input bar */}
            {supReplyAttachments.length > 0 && (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', padding: '8px 12px 0', borderTop: '1px solid var(--admin-border)' }}>
                {supReplyAttachments.map((f, i) => (
                  <div key={i} style={{ position: 'relative', width: '56px', height: '56px', borderRadius: '6px', overflow: 'hidden', border: '1px solid var(--admin-border)' }}>
                    <img src={URL.createObjectURL(f)} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <button style={{ position: 'absolute', top: '2px', right: '2px', width: '16px', height: '16px', borderRadius: '50%', background: 'rgba(0,0,0,0.75)', color: '#fff', border: 'none', fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }} onClick={() => setSupReplyAttachments(prev => prev.filter((_, idx) => idx !== i))}>&times;</button>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', padding: '10px 12px', borderTop: '1px solid var(--admin-border)', background: 'var(--admin-surface)' }}>
              <label className="admin-btn admin-btn--ghost admin-btn--sm" style={{ cursor: 'pointer', flexShrink: 0 }}>
                <i className="fa-solid fa-paperclip" />
                <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" multiple hidden onChange={handleSupReplyFileSelect} />
              </label>
              <textarea className="admin-input" ref={supReplyTextareaRef} value={supReplyMsg} onChange={e => {
                  setSupReplyMsg(e.target.value);
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px';
                  if (supSelectedTicket) {
                    clearTimeout(supTypingDebounceRef.current);
                    supTypingDebounceRef.current = setTimeout(() => {
                      apiFetch(`/api/admin/support/tickets/${supSelectedTicket.id}/typing`, { method: 'POST' }).catch(() => {});
                    }, 400);
                  }
                }}
                onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); document.getElementById('sup-send-btn').click(); } }}
                rows={1} placeholder="Type a reply… (Ctrl+Enter to send)"
                style={{ flex: 1, resize: 'none', margin: 0, minHeight: '36px', maxHeight: '200px', overflowY: 'auto', padding: '8px 12px', fontSize: '13px' }} />
              <button id="sup-send-btn" className="admin-btn admin-btn--primary admin-btn--sm" disabled={supReplySending || (!supReplyMsg.trim() && supReplyAttachments.length === 0)}
                onClick={async () => {
                  if (!supReplyMsg.trim() && supReplyAttachments.length === 0) return;
                  setSupReplySending(true);
                  try {
                    const fd = new FormData();
                    fd.append('message', supReplyMsg);
                    supReplyAttachments.forEach(f => fd.append('attachments', f));
                    await apiFetch(`/api/admin/support/tickets/${supSelectedTicket.id}/replies`, { method: 'POST', body: fd });
                    setSupReplyMsg(''); setSupReplyAttachments([]); if (supReplyTextareaRef.current) { supReplyTextareaRef.current.style.height = '36px'; }
                    await reloadRepliesSilent(supSelectedTicket.id);
                    scrollSupToBottom();
                    toast.success('Sent', 'Reply sent');
                  } catch { toast.error('Error', 'Reply failed'); }
                  finally { setSupReplySending(false); }
                }}>
                {supReplySending ? <i className="fa-solid fa-spinner fa-spin" /> : <i className="fa-solid fa-paper-plane" />}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="admin-card">
          <div style={{ marginBottom: '12px' }}>
            <div style={{ position: 'relative', maxWidth: '480px' }}>
              <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--admin-text-muted)', fontSize: '13px', pointerEvents: 'none' }} />
              <input
                className="admin-input"
                style={{ width: '100%', paddingLeft: '34px' }}
                placeholder="Search all tickets — subject, messages, email…"
                value={supSearch}
                onChange={e => onSearchChange(e.target.value)}
              />
              {supSearch && (
                <button onClick={() => onSearchChange('')} title="Clear search" style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--admin-text-muted)', cursor: 'pointer' }}>
                  <i className="fa-solid fa-times" />
                </button>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              <div className="admin-filter-pills" style={{ marginBottom: 0 }}>
                {['all', 'unread', 'open', 'in_progress', 'resolved', 'closed'].map(s => (
                  <button key={s} className={`admin-pill ${supStatusFilter === s ? 'admin-pill--active' : ''}`} onClick={() => setSupStatusFilter(s)}>
                    {s === 'in_progress' ? 'In Progress' : s === 'unread' ? `Unread${unreadInList ? ` (${unreadInList})` : ''}` : s}
                  </button>
                ))}
              </div>
              <div className="admin-filter-pills" style={{ marginBottom: 0 }}>
                {['all', 'low', 'medium', 'high'].map(p => (
                  <button key={p} className={`admin-pill ${supPriorityFilter === p ? 'admin-pill--active' : ''}`} onClick={() => setSupPriorityFilter(p)}>{p}</button>
                ))}
              </div>
            </div>
            <button className="admin-btn admin-btn--primary admin-btn--sm" onClick={() => setSupShowCreate(true)}>
              <i className="fa-solid fa-plus" style={{ marginRight: '4px' }} /> New Ticket
            </button>
          </div>
          <DataTable columns={ticketColumns} data={filteredTickets} loading={supLoading} emptyMessage="No tickets found" emptyIcon="fa-solid fa-inbox" />
        </div>
      )}

      {/* Create Ticket Modal */}
      {supShowCreate && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }} onClick={() => setSupShowCreate(false)}>
          <div className="admin-card" style={{ width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto', margin: 0 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '15px', color: 'var(--admin-text-primary)' }}>Create Ticket on Behalf of Customer</h3>
              <button className="admin-btn admin-btn--ghost admin-btn--sm" onClick={() => setSupShowCreate(false)}>&times;</button>
            </div>
            <form onSubmit={handleCreateTicket}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <label className="admin-label">CUSTOMER EMAIL *</label>
                  <input className="admin-input" type="email" placeholder="customer@example.com" value={supCreateEmail} onChange={e => setSupCreateEmail(e.target.value)} required />
                </div>
                <div>
                  <label className="admin-label">SUBJECT *</label>
                  <input className="admin-input" placeholder="Ticket subject" value={supCreateSubject} onChange={e => setSupCreateSubject(e.target.value)} required />
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <label className="admin-label">CATEGORY</label>
                    <select className="admin-select" value={supCreateCategory} onChange={e => setSupCreateCategory(e.target.value)}>
                      <option value="general">General</option>
                      <option value="billing">Billing</option>
                      <option value="technical">Technical</option>
                      <option value="account">Account</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="admin-label">PRIORITY</label>
                    <select className="admin-select" value={supCreatePriority} onChange={e => setSupCreatePriority(e.target.value)}>
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <label className="admin-label">PLAN SLUG (optional)</label>
                    <input className="admin-input" placeholder="e.g. n8n-pro" value={supCreatePlanSlug} onChange={e => setSupCreatePlanSlug(e.target.value)} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="admin-label">DOMAIN (optional)</label>
                    <input className="admin-input" placeholder="e.g. site.oph.st" value={supCreateDomain} onChange={e => setSupCreateDomain(e.target.value)} />
                  </div>
                </div>
                <div>
                  <label className="admin-label">MESSAGE *</label>
                  <textarea className="admin-input" rows={4} placeholder="Describe the issue or request..." value={supCreateMessage} onChange={e => setSupCreateMessage(e.target.value)} required style={{ resize: 'vertical' }} />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
                <button type="button" className="admin-btn admin-btn--ghost" onClick={() => setSupShowCreate(false)}>Cancel</button>
                <button type="submit" className="admin-btn admin-btn--primary" disabled={supCreating}>{supCreating ? 'Creating...' : 'Create Ticket'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {supLightbox && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out' }} onClick={() => setSupLightbox(null)}>
          <button style={{ position: 'absolute', top: '20px', right: '24px', background: 'none', border: 'none', color: '#fff', fontSize: '32px', cursor: 'pointer', opacity: 0.8, zIndex: 10000 }} onClick={() => setSupLightbox(null)}>&times;</button>
          <img src={supLightbox} alt="Full size" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: '8px', boxShadow: '0 8px 32px rgba(0,0,0,0.5)', cursor: 'default' }} onClick={e => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}

export default AdminSupport;
